import React from 'react';
import { Link } from '@tanstack/react-router';
import { useDashboards } from '../../../../api/dashboard';
import { ChartNoAxesCombined } from 'lucide-react';
import type { Dashboard } from '../../../../api/dashboard';

import { useTranslation } from 'react-i18next';

const LIST_ITEM_CLASS =
  'group flex w-full items-center gap-3 rounded-lg pl-0 pr-3 py-2.5 text-left text-sm transition-all hover:bg-gray-50 dark:hover:bg-gray-800 hover:shadow-sm';
const ICON_WRAPPER_CLASS =
  'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 group-hover:bg-orange-100 group-hover:text-orange-700';

const CONTENT_WRAPPER_CLASS = 'w-max min-w-[260px] max-w-[360px]';

export const DashboardListHoverContent: React.FC = () => {
  const { t } = useTranslation('pleri/chat');
  const dashboardsQuery = useDashboards({
    limit: 20,
    sortBy: 'createdAt',
    sortOrder: 'desc',
  });

  const isLoading = dashboardsQuery.isLoading;
  const hasError = Boolean(dashboardsQuery.error);
  const dashboards: Dashboard[] = dashboardsQuery.data?.dashboards ?? [];
  const isEmpty = !isLoading && !hasError && dashboards.length === 0;

  return (
    <div className={CONTENT_WRAPPER_CLASS}>
      {isLoading && (
        <div className="flex flex-col items-center justify-center gap-2 px-4 py-8">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-200 border-t-orange-500 dark:border-gray-700" />
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {t('loadingDashboards')}
          </span>
        </div>
      )}
      {hasError && (
        <div className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
          {t('couldNotLoadDashboards')}
        </div>
      )}
      {isEmpty && (
        <div className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
          {t('noDashboardsYet')}
        </div>
      )}
      {!isLoading && !hasError && dashboards.length > 0 && (
        <div className="py-0.5">
          {dashboards.map((dashboard) => (
            <Link
              key={dashboard.dashboardId}
              to="/dashboard/$dashboardId"
              params={{ dashboardId: dashboard.dashboardId }}
              search={{
                tenantId: dashboard.tenantId,
                ownerId: dashboard.ownerId,
                scope: dashboard.scope,
              }}
              className={LIST_ITEM_CLASS}
              data-testid={`dashboard-hover-item-${dashboard.dashboardId}`}
            >
              <span className={ICON_WRAPPER_CLASS}>
                <ChartNoAxesCombined className="h-4 w-4 stroke-[1.8]" />
              </span>
              <span className="min-w-0 flex-1 truncate font-medium text-gray-900 dark:text-gray-100">
                {dashboard.title}
              </span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};
